import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  InputNumber,
  TimePicker,
} from "antd";
import {
  dosageTiming,
  isStatMedicine,
  medicineFrequency,
  medicineRoutes,
} from "../../../../helpers/medicineKeys";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import dayjs from "dayjs";

const { Option } = Select;
const format = "hh A";

const defaultTimes = {
  Once_Daily: ["08 AM"],
  Twice_Daily: ["08 AM", "08 PM"],
  Thrice_Daily: ["08 AM", "01 PM", "08 PM"],
  Four_Times_Daily: ["08 AM", "01 PM", "04 PM", "08 PM"],
};

const fullWidth = { width: "100%" };

const MedicineFields = ({
  editData,
  form,
  startMedicine,
  setStartMedicine,
  selectedFrequency,
  setSelectedFrequency,
  initial,
}) => {
  const [times, setTimes] = useState([]);
  const isStat =
    isStatMedicine(selectedFrequency) ||
    isStatMedicine(editData) ||
    startMedicine;

  const handleFrequencyChange = (value) => {
    setSelectedFrequency(value);
    const stat = isStatMedicine(value);
    if (typeof setStartMedicine === "function") {
      setStartMedicine(stat);
    }
    if (stat) {
      setTimes([]);
      form.setFieldsValue({
        schedule: { times: [] },
        duration: undefined,
        days_left_initial: undefined,
      });
      return;
    }
    const defaultTimeValues = defaultTimes[value] || [];
    setTimes(defaultTimeValues.map((time) => dayjs(time, format)));
    form.setFieldsValue({
      schedule: { times: defaultTimeValues.map((time) => dayjs(time, format)) },
    });
  };

  const handleTimeChange = (time, index) => {
    const newTimes = [...times];
    newTimes[index] = time;
    setTimes(newTimes);
    form.setFieldsValue({
      schedule: { times: newTimes },
    });
  };

  useEffect(() => {
    if (editData?.frequency) {
      setSelectedFrequency(editData?.frequency);
      const editTimes = editData?.schedule?.times
        ? editData?.schedule?.times?.map((time) => dayjs(time, "HH"))
        : times;
      setTimes(editTimes);
      form.setFieldsValue({
        schedule: { times: editTimes },
      });
    }
  }, [editData?.frequency]);

  const timePickerCount = {
    Once_Daily: 1,
    Twice_Daily: 2,
    Thrice_Daily: 3,
    Four_Times_Daily: 4,
  }[selectedFrequency];

  const timeColSpan =
    timePickerCount === 1
      ? 24
      : timePickerCount === 2
      ? 12
      : timePickerCount === 3
      ? 8
      : timePickerCount === 4
      ? 6
      : 24;

  const renderTimePickers = () => {
    if (!timePickerCount || isStat) return null;

    return (
      <Row gutter={[12, 0]} style={{ marginTop: 4 }}>
        {Array.from({ length: timePickerCount }, (_, index) => (
          <Col span={timeColSpan} key={index}>
            <Form.Item
              name={["schedule", "times", index]}
              style={{ marginBottom: 0 }}
            >
              <TimePicker
                inputReadOnly
                needConfirm={false}
                value={times[index]}
                format={format}
                onChange={(time) => handleTimeChange(time, index)}
                style={fullWidth}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Row gutter={[16, 8]}>
        <Col xs={24} lg={14}>
          <Form.Item label="Drug" name="drug">
            <Input className={inputStyles.mdInput} style={fullWidth} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Form.Item label="Dose" name="dose">
            <Input
              placeholder="Dose"
              className={inputStyles.mdInput}
              style={fullWidth}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Form.Item label="Route" name="route">
            <Select className={inputStyles.mdSelect} style={fullWidth}>
              {medicineRoutes?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 8]}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Frequency"
            name="frequency"
            rules={[{ required: true, message: "Please choose frequency" }]}
            style={{ marginBottom: timePickerCount && !isStat ? 8 : 24 }}
          >
            <Select
              onChange={handleFrequencyChange}
              className={inputStyles.mdSelect}
              style={fullWidth}
              placeholder="Select frequency"
            >
              {medicineFrequency?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {renderTimePickers()}
          {selectedFrequency === "Every_X_Hours" && (
            <Form.Item label="Every (hours)" name={["schedule", "time"]}>
              <InputNumber
                min={0}
                max={24}
                className={inputStyles.mdNumberInput}
                style={fullWidth}
              />
            </Form.Item>
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dosage_timing" label="Timing">
            <Select
              className={inputStyles.mdSelect}
              style={fullWidth}
              placeholder="Select timing"
            >
              {dosageTiming?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {!isStat && (
          <Col xs={24} sm={12} md={8}>
            {initial ? (
              <Form.Item label="Days Left" name="days_left_initial">
                <InputNumber
                  className={inputStyles.mdNumberInput}
                  style={fullWidth}
                  min={0}
                />
              </Form.Item>
            ) : (
              <Form.Item label="Duration (days)" name="duration">
                <InputNumber
                  className={inputStyles.mdNumberInput}
                  style={fullWidth}
                  min={0}
                />
              </Form.Item>
            )}
          </Col>
        )}
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Form.Item label="Notes" name="comments" style={{ marginBottom: 0 }}>
            <Input.TextArea
              className={inputStyles.mdInput}
              rows={3}
              placeholder="Enter notes"
              style={fullWidth}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default MedicineFields;
